import { Checkbox, Radio, Shadow, Switch, Text, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { InterfaceRadioProps } from 'native-base/lib/typescript/components/primitives/Radio/types';
import React from 'react';
import { useRecoilValue } from 'recoil';

import { useConsortiumActions } from '../../../../store/consortium/actions';
import {
  customerConsortiumChannelsSelectedState,
  customerConsortiumRadioSelectedState,
  customerConsortiumSectionStatusState,
} from '../../../../store/consortium/atom';
import {
  customerConsortiumChannelOptions,
  customerConsortiumRadioOptions,
} from '../../../../store/consortium/constants';
import { customerConsortiumPlanIsSelectedSelector } from '../../../../store/consortium/selector';
import { participateConversationComponentIsVisible } from '../../utils';

interface CompleteProps {}

export const Complete: React.FC<CompleteProps> = () => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const selectedChannels = useRecoilValue(customerConsortiumChannelsSelectedState);
  const selectedRadio = useRecoilValue(customerConsortiumRadioSelectedState);
  const customerConsortiumPlanIsSelected = useRecoilValue(customerConsortiumPlanIsSelectedSelector);
  const customerSectionStatus = useRecoilValue(customerConsortiumSectionStatusState);
  const isCustomerSectionClosed = customerSectionStatus === 'closed';

  const participateConversationIsSelected = selectedRadio === '1';

  const { handleSelectCustomerConsortiumChannels, handleSelectCustomerConsortiumRadio } =
    useConsortiumActions();

  const onChannelSelectChange = (value: string, selected: boolean) => {
    if (selected) {
      return handleSelectCustomerConsortiumChannels([...selectedChannels, value]);
    }
    const filteredOptions = selectedChannels?.filter((channel) => channel !== value);
    handleSelectCustomerConsortiumChannels(filteredOptions);
  };

  if (!customerConsortiumPlanIsSelected || !isCustomerSectionClosed) {
    return null;
  }

  return (
    <Shadow
      level={1}
      borderTopRadius={BoxRadius.sm}
      {...(isMobile && { borderBottomRadius: BoxRadius.sm })}>
      <VStack
        py={{ base: 0, sm: Spacing['stack.xxs'] }}
        px={Spacing['inline.xxs']}
        borderBottomWidth={{ base: 0, sm: '1px' }}
        borderBottomColor="neutral.light"
        {...(isMobile && { pt: Spacing['inline.xxs'], pb: Spacing['inline.nano'] })}>
        <Text
          fontSize={Typography['fontsize.lg']}
          variant="semibold"
          color="brand.primary.principal">
          Conclua sua recomendação
        </Text>
      </VStack>
      <Stack
        direction={{ base: 'column', sm: 'row' }}
        py={{ base: 0, sm: Spacing['inline.xxxs'] }}
        px={Spacing['inline.xxs']}
        alignItems={{ base: 'flex-start', sm: 'center' }}
        justifyContent={'space-between'}
        {...(isMobile && { space: Spacing['stack.xxxs'], pb: Spacing['inline.xxs'] })}>
        {participateConversationComponentIsVisible && !isMobile && (
          <VStack space={Spacing['inline.nano']}>
            <Text
              variant="semibold"
              fontSize={Typography['fontsize.md']}
              color={'neutral.black'}
              lineHeight={'distant'}>
              Deseja participar da conversa com o especialista?
            </Text>
            <Radio
              testId="participate-conversation"
              options={customerConsortiumRadioOptions}
              onChange={handleSelectCustomerConsortiumRadio}
              selectedValue={selectedRadio}
              radioComponentProps={{ padding: 0.5 } as InterfaceRadioProps}
              textComponentProps={{ color: 'neutral.black' }}
            />
          </VStack>
        )}
        {participateConversationComponentIsVisible && isMobile && (
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <Text variant={'regular'} fontSize={Typography['fontsize.md']} color={'neutral.black'}>
              Participar da conversa ?
            </Text>
            <Switch
              onToggle={(value: boolean) => {
                const parsedValue = value ? '1' : '0';
                handleSelectCustomerConsortiumRadio(parsedValue);
              }}
              isChecked={participateConversationIsSelected}
            />
          </HStack>
        )}
        {isMobile &&
          customerConsortiumChannelOptions.map((option) => (
            <HStack key={option.label} justifyContent="space-between" alignItems="center" w="full">
              <Text
                variant={'regular'}
                fontSize={Typography['fontsize.md']}
                color={'neutral.black'}>
                {option.label}
              </Text>
              <Switch
                onToggle={(value: boolean) => onChannelSelectChange(option.value, value)}
                isChecked={selectedChannels?.includes(option.value)}
                isDisabled={!!option.disabled}
              />
            </HStack>
          ))}
        <HStack
          space={Spacing['inline.xxs']}
          {...(isMobile && { w: 'full' })}
          {...(!participateConversationComponentIsVisible && {
            w: 'full',
            alignItems: 'center',
            justifyContent: 'space-between',
          })}>
          {!isMobile && (
            <VStack space={Spacing['inline.nano']}>
              <Text
                variant="semibold"
                fontSize={Typography['fontsize.md']}
                color={'neutral.black'}
                lineHeight={'distant'}>
                Selecione o canal de envio
              </Text>
              <Checkbox
                hasError={false}
                testId="channel-options"
                options={customerConsortiumChannelOptions}
                onChange={onChannelSelectChange}
                selectedValues={selectedChannels}
              />
            </VStack>
          )}

          <Stack
            {...(isMobile && { w: 'full', justifyContent: 'space-between' })}
            direction={{ base: 'row', sm: 'column' }}
            space={Spacing['inline.nano']}
            alignItems={{ base: 'center', sm: 'flex-end' }}>
            <Text
              variant={{ base: 'regular', sm: 'semibold' }}
              fontSize={Typography['fontsize.md']}
              color={'neutral.black'}>
              Data de Envio
            </Text>
            <Text variant="regular" fontSize={Typography['fontsize.md']} color={'neutral.black'}>
              {Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy')}
            </Text>
          </Stack>
        </HStack>
      </Stack>
    </Shadow>
  );
};
